import { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import {
  Plus,
  Building2,
  MoreHorizontal,
  Pencil,
  Trash2,
  Users,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  PageHeader,
  TableFilters,
  DataTable,
  Column,
  StatusBadge,
  ConfirmDialog,
  MetricCard,
  KPIGrid,
} from '@/components/common';
import { companiesApi, Company, QueryCompanyParams } from '@/api/services';
import { getErrorMessage } from '@/api/client';

export default function Companies() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; company?: Company }>({
    open: false,
  });
  const [deleting, setDeleting] = useState(false);

  const fetchCompanies = async (params?: QueryCompanyParams) => {
    try {
      setLoading(true);
      const response = await companiesApi.getAll(params);
      setCompanies(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies({ search: searchQuery || undefined });
  }, [searchQuery]);

  const handleDelete = async () => {
    if (!deleteDialog.company) return;

    try {
      setDeleting(true);
      await companiesApi.delete(deleteDialog.company.id);
      toast.success('Company deleted successfully');
      setDeleteDialog({ open: false });
      fetchCompanies({ search: searchQuery || undefined });
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setDeleting(false);
    }
  };

  const metrics = useMemo(() => {
    const total = companies.length;
    const active = companies.filter((c) => c.status === 'ACTIVE').length;
    const suspended = companies.filter((c) => c.status === 'SUSPENDED').length;
    const totalUsers = companies.reduce((sum, c) => sum + (c._count?.users || 0), 0);

    return { total, active, suspended, totalUsers };
  }, [companies]);

  const columns: Column<Company>[] = [
    {
      key: 'slug',
      header: 'Slug',
      sortable: true,
      render: (company) => <span className="whitespace-nowrap font-mono">{company.slug}</span>,
    },
    {
      key: 'name',
      header: 'Company',
      sortable: true,
      render: (company) => <span className="truncate font-semibold">{company.displayName}</span>,
    },
    {
      key: 'type',
      header: 'Type',
      render: (company) => (
        <StatusBadge
          status={company.type === 'PRIMARY_ENTERPRISE' ? 'active' : 'pending'}
          label={company.type === 'PRIMARY_ENTERPRISE' ? 'Enterprise' : 'Partner'}
        />
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (company) => <StatusBadge status={company.status.toLowerCase() as any} />,
    },
    {
      key: '_count',
      header: 'Users',
      render: (company) => company._count?.users || 0,
    },
    {
      key: 'actions',
      header: '',
      className: 'w-12',
      render: (company) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="More actions" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => setDeleteDialog({ open: true, company })}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          title="Companies"
          description="Tenant organisations on the platform, with type, status, and registered account count."
          actions={
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add Company
            </Button>
          }
        />
      </motion.div>

      <div>
        <KPIGrid className="mt-6">
          <MetricCard
            label="Total Companies"
            value={metrics.total}
            sentiment="info"
            icon={<Building2 className="h-5 w-5" />}
          />
          <MetricCard
            label="Active"
            value={metrics.active}
            sentiment="success"
            icon={<CheckCircle2 className="h-5 w-5" />}
          />
          <MetricCard
            label="Suspended"
            value={metrics.suspended}
            sentiment={metrics.suspended > 0 ? 'danger' : 'neutral'}
            icon={<XCircle className="h-5 w-5" />}
          />
          <MetricCard
            label="Total Users"
            value={metrics.totalUsers}
            sentiment="neutral"
            icon={<Users className="h-5 w-5" />}
          />
        </KPIGrid>
      </div>

      <motion.div variants={fadeUp} className="mt-6">
        <DataTable
          toolbar={
            <TableFilters
              search={searchQuery}
              onSearchChange={setSearchQuery}
              searchPlaceholder="Search companies..."
            />
          }
          data={companies}
          columns={columns}
          loading={loading}
          pagination
          exportable
          columnToggle
          emptyTitle="No companies found"
          emptyDescription="Get started by creating your first company."
          emptyAction={
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add Company
            </Button>
          }
        />
      </motion.div>

      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open })}
        title="Delete Company"
        description={`Are you sure you want to delete "${deleteDialog.company?.displayName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={handleDelete}
        variant="destructive"
        loading={deleting}
      />
    </motion.main>
  );
}
